import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, FileDown, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const StaffLeaveReport = () => {
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [summary, setSummary] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);

  useEffect(() => {
    fetchReport();
    apiClient.get('/api/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      // Fetch leave applications from correct API endpoint
      const [applicationsRes, typesRes] = await Promise.all([
        apiClient.get('/api/leave-management/all-leave-applications').catch(() => ({ data: [] })),
        apiClient.get('/api/leave-management/leave-types').catch(() => ({ data: [] }))
      ]);
      
      const applications = Array.isArray(applicationsRes.data) ? applicationsRes.data : [];
      const types = Array.isArray(typesRes.data) ? typesRes.data : [];
      
      setLeaves(applications);
      setLeaveTypes(types);
      
      // Calculate summary
      const approved = applications.filter(l => l.status?.toLowerCase() === 'approved').length;
      const pending = applications.filter(l => l.status?.toLowerCase() === 'pending').length;
      const rejected = applications.filter(l => l.status?.toLowerCase() === 'rejected').length;
      setSummary({ total: applications.length, approved, pending, rejected });
    } catch (error) {
      console.error('Error:', error);
      setLeaves([]);
      setSummary({ total: 0, approved: 0, pending: 0, rejected: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getLeaveTypeName = (leaveTypeId) => {
    const type = leaveTypes.find(t => t.id === leaveTypeId);
    return type?.name || type?.short_code || leaveTypeId || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportToPDF = () => {
    const stats = {
      'Total Leaves': summary.total,
      'Approved': summary.approved,
      'Pending': summary.pending,
      'Rejected': summary.rejected
    };

    const columns = [
      { header: 'Staff Name', dataKey: 'name' },
      { header: 'Leave Type', dataKey: 'type' },
      { header: 'From', dataKey: 'from' },
      { header: 'To', dataKey: 'to' },
      { header: 'Days', dataKey: 'days' },
      { header: 'Status', dataKey: 'status' }
    ];

    const data = leaves.map(l => ({
      name: l.staff_name || 'N/A',
      type: getLeaveTypeName(l.leave_type_id),
      from: l.from_date || 'N/A',
      to: l.to_date || 'N/A',
      days: l.total_days || l.number_of_days || 0,
      status: l.status || 'N/A'
    }));

    exportSummaryReportToPDF('Staff Leave Report', stats, columns, data, 'staff_leave_report.pdf', collegeInfo);
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Staff Leave Report
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={leaves.length === 0}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Leaves</p>
                  <p className="text-2xl font-bold text-blue-700">{summary.total}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-700">{summary.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-700">{summary.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-700">{summary.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>

          {/* Leave Types Summary */}
          {leaveTypes.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Leave Types Available</h3>
              <div className="flex flex-wrap gap-2">
                {leaveTypes.map((type, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 text-sm rounded-full border"
                    style={{ backgroundColor: type.color + '20', borderColor: type.color, color: type.color }}
                  >
                    {type.short_code}: {type.name} ({type.annual_quota} days)
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Leave Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Staff Name</th>
                  <th className="text-left p-3">Leave Type</th>
                  <th className="text-left p-3">From Date</th>
                  <th className="text-left p-3">To Date</th>
                  <th className="text-center p-3">Days</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">Applied On</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {leaves.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No leave applications found</p>
                      <p className="text-sm mt-1">Staff can apply for leave from Leave Management module</p>
                    </td>
                  </tr>
                ) : (
                  leaves.map((leave, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{leave.staff_name || 'N/A'}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {getLeaveTypeName(leave.leave_type_id)}
                        </span>
                      </td>
                      <td className="p-3">{leave.from_date || 'N/A'}</td>
                      <td className="p-3">{leave.to_date || 'N/A'}</td>
                      <td className="p-3 text-center font-semibold">{leave.total_days || leave.number_of_days || 0}</td>
                      <td className="p-3 text-sm text-gray-600 max-w-[200px] truncate" title={leave.reason}>
                        {leave.reason || '-'}
                      </td>
                      <td className="p-3 text-sm text-gray-500">
                        {leave.applied_at ? formatDate(leave.applied_at) : 'N/A'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(leave.status)}`}>
                          {leave.status || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLeaveReport;
